// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import PlusCircle from "@insite/mobius/Icons/PlusCircle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button from "@store/MobiusOverrides/Button/Button";
import StepFormInputContext from "@store/Framework/Store/Context/StepFormInputContext";
import {
    CreditFormStepProps,
    creditFormStepStyles,
} from "@store/Library/Components/CreditForm/StepComponents/BasicDetailsForm";
import CreditFormTradeReference from "@store/Library/Components/CreditForm/StepComponents/CreditFormTradeReference";
import CardBodyComponent from "@store/Library/Widgets/Card/CardBody";
import useCreditFormTradeReferences, {
    TradeReference,
} from "@store/Library/Widgets/CreditForm/useCreditFormTradeReferences";
import { ButtonIcon } from "@store/MobiusOverrides/Button/Props";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import uniqueId from "lodash/uniqueId";
import React, { useContext, useMemo, useRef } from "react";
import { css } from "styled-components";
import { CreditFormContext } from "../../../Widgets/CreditForm/CreditFormContextContainer";

interface TradeReferenceFormStyles {
    buttonWrapper?: InjectableCss;
    button: InjectableCss;
}

const tradeReferenceFormStyles: TradeReferenceFormStyles = {
    buttonWrapper: {
        css: css`
            display: flex;
            width: 100%;
            justify-content: center;
            align-items: center;
        `,
    },
    button: {
        css: css`
            color: ${getColor("primary")};
            border: none;
            :disabled {
                background: transparent;
            }
            ${ButtonIcon} {
                margin-right: ${pxToRem(12)};
                height: ${pxToRem(32)};

                width: ${pxToRem(32)};
                > svg {
                    height: ${pxToRem(32)};
                    width: ${pxToRem(32)};
                }
            }
        `,
    },
};
const TradeReferencesForm: React.FC<CreditFormStepProps> = ({ hasSubmitted, editable = true, stepIndex }) => {
    const { numberOfTradeReferences = 2 } = useContext(CreditFormContext) ?? {};
    const creditFormTradeReferences = useCreditFormTradeReferences(stepIndex ?? undefined, numberOfTradeReferences);

    // need to generate unique ids for each trade reference because the index is not stable
    const ids = useRef(creditFormTradeReferences.tradeReferences?.map(() => uniqueId()) ?? []);
    if (ids.current.length !== creditFormTradeReferences.tradeReferences?.length) {
        ids.current = creditFormTradeReferences.tradeReferences?.map(() => uniqueId() ?? []);
    }
    const { tradeReferences, handleTradeReferenceChange, handleTradeReferenceDelete, handleAddTradeReference } =
        creditFormTradeReferences;
    const stepFormInputContextData = useMemo(() => {
        return {
            editable,
            hasSubmitted,
            currentStep: stepIndex,
        };
    }, [editable, hasSubmitted, stepIndex]);
    if (!tradeReferences) {
        return null;
    }

    return (
        <StepFormInputContext.Provider value={stepFormInputContextData}>
            <CardBodyComponent extendedStyles={creditFormStepStyles.cardBody}>
                {tradeReferences.map((tradeReference, index) => {
                    return (
                        <CreditFormTradeReference
                            editable={editable}
                            tradeReference={tradeReference}
                            onInputChange={(key: keyof TradeReference, event) =>
                                handleTradeReferenceChange(index, key, event)
                            }
                            canDelete={tradeReferences.length > numberOfTradeReferences}
                            key={ids.current[index]}
                            onDeleteClick={() => handleTradeReferenceDelete(tradeReference)}
                            index={index}
                        />
                    );
                })}

                {editable && (
                    <StyledWrapper {...tradeReferenceFormStyles.buttonWrapper}>
                        <Button
                            icon={{ src: PlusCircle, position: "left" }}
                            sizeVariant="medium"
                            title={tradeReferences.length >= 5 ? "Maximum of 5 trade references" : undefined}
                            mergeCss
                            {...tradeReferenceFormStyles.button}
                            variant="tertiary"
                            disabled={tradeReferences.length >= 5}
                            onClick={handleAddTradeReference}
                        >
                            {translate("Trade Reference")}
                        </Button>
                    </StyledWrapper>
                )}
            </CardBodyComponent>
        </StepFormInputContext.Provider>
    );
};

export default TradeReferencesForm;
